import React, {useEffect, useMemo, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from '../../../constants/colors'
import {ComplianceStatus, UserComplianceListItemModel} from '../../../redux/companyMonitorTypes'
import CountryFlag from '../../CountryFlag'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {useNavigate} from 'react-router-dom'
import Badge from "react-bootstrap/Badge";
import {formatDateOnlyLocale} from "../../../utils/dateHelper";
import InputField from "../../InputField";
import Select from "../../Select";
import ScoreBar from "./ScoreBar";

interface Props {
  items: UserComplianceListItemModel[]
}

const sortingStorageKey = 'cappy-backoffice:aml-users:sorting'
const sortableColumnIds = new Set([
  'name',
  'identificationNumber',
  'country',
  'dateOfBirth',
  'initialFlagDate',
  'lastDecisionBy',
  'score',
  'complianceStatus',
  'lastDecisionReason',
  'lastDecisionInvestigationReference',
])

const getStoredSorting = (): SortingState => {
  if (typeof window === 'undefined') return []

  try {
    const storedSorting = JSON.parse(window.localStorage.getItem(sortingStorageKey) ?? '[]') as unknown

    if (!Array.isArray(storedSorting)) return []

    const isSortingState = storedSorting.every((sort) => (
      typeof sort === 'object'
      && sort !== null
      && 'id' in sort
      && typeof sort.id === 'string'
      && sortableColumnIds.has(sort.id)
      && 'desc' in sort
      && typeof sort.desc === 'boolean'
    ))

    return isSortingState ? storedSorting as SortingState : []
  } catch {
    return []
  }
}

const UserComplianceList = ({items}: Props) => {
  const styles = useStyles()
  const navigate = useNavigate()

  const [sorting, setSorting] = useState<SortingState>(getStoredSorting)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState<string>('')

  useEffect(() => {
    try {
      window.localStorage.setItem(sortingStorageKey, JSON.stringify(sorting))
    } catch {
      // Sorting still works when storage is unavailable.
    }
  }, [sorting])

  const getComplianceBadge = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return <Badge text="dark" bg={"success"}>Compliant</Badge>
      case 'nonCompliant':
        return <Badge text="dark" bg={"danger"}>Non-compliant</Badge>
      case 'unknown':
      default:
        return <Badge text="dark" bg={"warning"}>Decision required</Badge>
    }
  }

  const columns = useMemo<ColumnDef<UserComplianceListItemModel>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => `${row.givenName ?? ''} ${row.familyName ?? ''}`.trim(),
      cell: ({row}) => (
        <span>{`${row.original.givenName} ${row.original.familyName}`}</span>
      ),
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
    },
    {
      id: 'identificationNumber',
      header: 'Identification number',
      accessorFn: (row) => row.identificationNumber ?? '',
      cell: ({row}) => <span>{row.original.identificationNumber}</span>,
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
    },
    {
      id: 'country',
      header: 'Country',
      accessorFn: (row) => row.country ?? '',
      cell: ({row}) => <span className={styles.uppercase}>
        {row.original.country ? <><CountryFlag country={row.original.country}/>{' '}{row.original.country}</> : '-'}
      </span>,
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
    },
    {
      id: 'dateOfBirth',
      header: 'Date of birth',
      accessorFn: (row) => row.dateOfBirth ?? '',
      cell: ({row}) => <span>{formatDateOnlyLocale(row.original.dateOfBirth)}</span>,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue<string>(columnId)
        const b = rowB.getValue<string>(columnId)
        const at = a ? new Date(a).getTime() : Number.POSITIVE_INFINITY
        const bt = b ? new Date(b).getTime() : Number.POSITIVE_INFINITY
        return at - bt
      },
    },
    {
      id: 'initialFlagDate',
      header: 'Initial flag date',
      accessorFn: (row) => row.initialFlagDate ?? '',
      cell: ({row}) => <span>{formatDateOnlyLocale(row.original.initialFlagDate) ?? '-'}</span>,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue<string>(columnId)
        const b = rowB.getValue<string>(columnId)
        const at = a ? new Date(a).getTime() : Number.POSITIVE_INFINITY
        const bt = b ? new Date(b).getTime() : Number.POSITIVE_INFINITY
        return at - bt
      },
    },
    {
      id: 'lastDecisionBy',
      header: 'Last decision by',
      accessorFn: (row) => row.lastDecisionBy ?? '',
      cell: ({row}) => <span>{row.original.lastDecisionBy ?? '-'}</span>,
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
    },
    {
      id: 'score',
      header: 'Score',
      accessorFn: (row) => row.score,
      cell: ({row}) => <ScoreBar score={row.original.score} format="decimal" />,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue<number | null>(columnId)
        const b = rowB.getValue<number | null>(columnId)
        const av = typeof a === 'number' ? a : Number.POSITIVE_INFINITY
        const bv = typeof b === 'number' ? b : Number.POSITIVE_INFINITY
        return av - bv
      },
      filterFn: (row, columnId, filterValue) => {
        const min = Number(filterValue)
        if (!Number.isFinite(min)) return true
        const value = row.getValue<number | null>(columnId)
        if (typeof value !== 'number') return false
        return value >= min
      },
    },
    {
      id: 'complianceStatus',
      header: 'Compliance status',
      accessorFn: (row) => row.complianceStatus ?? 'unknown',
      cell: ({row}) => (
        <div style={{display: 'flex', alignItems: 'center'}}>
          {getComplianceBadge(row.original.complianceStatus)}
        </div>
      ),
      filterFn: 'equalsString',
      sortingFn: 'alphanumeric',
    },
    {
      id: 'lastDecisionReason',
      header: 'Last decision reason',
      accessorFn: (row) => row.lastDecisionReason ?? '',
      filterFn: 'includesString',
    },
    {
      id: 'lastDecisionInvestigationReference',
      header: 'Last decision investigation ref',
      accessorFn: (row) => row.lastDecisionInvestigationReference ?? '',
      filterFn: 'includesString',
    },
  ], [styles.uppercase])

  const table = useReactTable({
    data: items ?? [],
    columns,
    initialState: {
      columnVisibility: {
        lastDecisionReason: false,
        lastDecisionInvestigationReference: false,
      },
    },
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    globalFilterFn: 'includesString',
  })

  const complianceFilterValue = (table.getColumn('complianceStatus')?.getFilterValue() as string) ?? ''
  const lastDecisionByFilterValue = (table.getColumn('lastDecisionBy')?.getFilterValue() as string) ?? ''
  const minScoreFilterValue = (table.getColumn('score')?.getFilterValue() as string) ?? ''

  return (
    <div className={styles.container}>
      <div className={styles.filtersRow}>
        <InputField
          id={'searchInput'}
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search (name, ID, country...)"
        />

        <Select
          id='complianceFilter'
          key='complianceFilter'
          options={[
            {name: 'All statuses', value: ''},
            {name: 'Compliant', value: 'compliant'},
            {name: 'Non-compliant', value: 'nonCompliant'},
            {name: 'Decision required', value: 'unknown'},
          ]}
          selectedValue={complianceFilterValue}
          onSelect={(value) => table.getColumn('complianceStatus')?.setFilterValue(value)}
        />

        <InputField
          id={'lastDecisionByFilter'}
          value={lastDecisionByFilterValue}
          onChange={(e) => table.getColumn('lastDecisionBy')?.setFilterValue(e.target.value)}
          placeholder="Filter: last decision by"
        />

        <InputField
          id={'minScoreFilter'}
          value={minScoreFilterValue}
          numeric
          onChange={(e) => table.getColumn('score')?.setFilterValue(e.target.value)}
          placeholder="Filter: min score"
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort()
                const sortDir = header.column.getIsSorted() // false | 'asc' | 'desc'

                return (
                  <th key={header.id} className={styles.headerCell}>
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        className={styles.headerButton}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        aria-label={
                          canSort
                            ? `Sort by ${String(header.column.columnDef.header)}`
                            : undefined
                        }
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className={styles.sortIndicator}>
                            {sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : ''}
                          </span>
                        )}
                      </button>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={styles.bodyRow}
              onClick={() => navigate(`/aml/users/${row.original.id}`)}
              tabIndex={0}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={styles.bodyCell}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default UserComplianceList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  filtersRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerRow: {
    backgroundColor: 'transparent',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    borderRadius: '4px',
    fontWeight: 'bold',
    color: Colors.darkGray2,
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  headerButton: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    userSelect: 'none',
  },
  sortIndicator: {
    opacity: 0.8,
  },
  bodyRow: {
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover $bodyCell': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    transition: '0.4s',

    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  uppercase: {
    textTransform: 'uppercase',
  }
})
